import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { AdventureTaskCard } from '../../components/student/adventure/AdventureTaskCard';
import { BadgeWallDialog } from '../../components/student/adventure/BadgeWallDialog';
import { LockedAdventureCard } from '../../components/student/adventure/LockedAdventureCard';
import { AdventureSessionSummaryDto } from '../../types/adventure';
import {
  buildBadgeSlotModels,
  buildLockedAdventureCards,
  getCompletedAdventureStepCount,
  getAdventureTaskImageSrc,
  getEarnedAdventureBadgeNames,
} from '../../utils/adventurePresentation';

interface Badge {
  id: string;
  name: string;
  icon: string;
  order?: number;
}

interface Assignment {
  id: string;
  title: string;
  description: string;
  dueDate: string | null;
  badges: Badge[];
}

const isAdventureAssignment = (assignment: Assignment) => assignment.title.includes('探险');
const ADVENTURE_SYSTEM_NAME = '神奇的探险之旅';
const LOCKED_ADVENTURE_CARDS = buildLockedAdventureCards();

const normalizeSessions = (value: unknown): AdventureSessionSummaryDto[] =>
  Array.isArray(value)
    ? value.filter(
        (item): item is AdventureSessionSummaryDto =>
          Boolean(item) &&
          typeof item === 'object' &&
          'currentStep' in item &&
          'completedSteps' in item
      )
    : [];

export const StudentDashboard: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [sessionSummaries, setSessionSummaries] = useState<Record<string, AdventureSessionSummaryDto[]>>({});
  const [badgeWallAssignment, setBadgeWallAssignment] = useState<Assignment | null>(null);
  const [previewImageTitle, setPreviewImageTitle] = useState('');
  const [previewImageSrc, setPreviewImageSrc] = useState('');

  const handleLogout = () => {
    dispatch(logout());
    navigate('/student/login');
  };

  useEffect(() => {
    let active = true;

    const loadAssignments = async () => {
      try {
        const response = await api.get<Assignment[]>('/assignments');
        if (!active) {
          return;
        }

        const nextAssignments = Array.isArray(response.data) ? response.data : [];
        setAssignments(nextAssignments);

        const adventureAssignments = nextAssignments.filter(isAdventureAssignment);
        if (adventureAssignments.length > 0) {
          const sessionResponses = await Promise.all(
            adventureAssignments.map(async (assignment) => {
              try {
                const result = await api.get<AdventureSessionSummaryDto[]>(
                  `/assignments/${assignment.id}/adventure-sessions`
                );

                return [assignment.id, normalizeSessions(result.data)] as const;
              } catch (error) {
                return [assignment.id, []] as const;
              }
            })
          );

          if (active) {
            setSessionSummaries(Object.fromEntries(sessionResponses));
          }
        }
      } catch (error) {
        if (active) {
          setLoadError('加载任务失败，请稍后重试');
        }
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    void loadAssignments();

    return () => {
      active = false;
    };
  }, []);

  const renderedAssignments = useMemo(
    () =>
      assignments.map((assignment) => {
        const sessions = sessionSummaries[assignment.id] ?? [];
        const latestSession = sessions[0];
        const safeBadges = Array.isArray(assignment.badges) ? assignment.badges : [];
        const earnedBadgeNames = getEarnedAdventureBadgeNames(latestSession, safeBadges);
        const earnedBadgeIds = new Set(
          safeBadges
            .filter((badge) => earnedBadgeNames.has(badge.name))
            .map((badge) => badge.id)
        );

        return {
          ...assignment,
          latestSession,
          badgeSlots: buildBadgeSlotModels({
            badges: safeBadges,
            earnedBadgeIds,
          }),
          progressCount: getCompletedAdventureStepCount(latestSession),
        };
      }),
    [assignments, sessionSummaries]
  );

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fafc,#eefbf4)] p-6">
      <div className="mx-auto max-w-5xl">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">你好, {user?.username} 👋</h1>
            <p className="text-gray-600">欢迎来到神奇的探险之旅，挑一张任务卡开始今天的冒险。</p>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-full bg-white px-4 py-2 text-gray-700 shadow hover:bg-gray-50"
          >
            退出
          </button>
        </header>

        {isLoading ? (
          <div className="text-center text-gray-500">正在加载任务...</div>
        ) : loadError ? (
          <div className="text-center text-red-500">{loadError}</div>
        ) : assignments.length === 0 ? (
          <div className="text-center text-gray-500">暂无可用任务</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {renderedAssignments
              .filter((assignment) => isAdventureAssignment(assignment))
              .slice(0, 1)
              .map((assignment) => (
                <AdventureTaskCard
                  key={assignment.id}
                  title={ADVENTURE_SYSTEM_NAME}
                  story="探险，赢得勋章！"
                  progressLabel={assignment.latestSession ? `已完成 ${assignment.progressCount}/5` : '准备出发'}
                  badgeSlots={assignment.badgeSlots}
                  imageSrc={getAdventureTaskImageSrc(ADVENTURE_SYSTEM_NAME)}
                  onPreviewImage={() => {
                    setPreviewImageTitle(ADVENTURE_SYSTEM_NAME);
                    setPreviewImageSrc(getAdventureTaskImageSrc(ADVENTURE_SYSTEM_NAME));
                  }}
                  onContinue={() => navigate(`/student/adventure-entry/${assignment.id}`)}
                  onViewBadges={() => setBadgeWallAssignment(assignment)}
                />
              ))}
            {LOCKED_ADVENTURE_CARDS.map((card) => (
              <LockedAdventureCard key={card.id} card={card} />
            ))}
          </div>
        )}
      </div>

      {badgeWallAssignment && (
        <BadgeWallDialog
          title={ADVENTURE_SYSTEM_NAME}
          badges={buildBadgeSlotModels({
            badges: badgeWallAssignment.badges,
            earnedBadgeIds: new Set(
              badgeWallAssignment.badges
                .filter((badge) =>
                  getEarnedAdventureBadgeNames(
                    (sessionSummaries[badgeWallAssignment.id] ?? [])[0],
                    badgeWallAssignment.badges
                  ).has(badge.name)
                )
                .map((badge) => badge.id)
            ),
          })}
          onClose={() => setBadgeWallAssignment(null)}
        />
      )}

      {previewImageSrc ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-6" role="dialog" aria-label="任务图片预览">
          <button
            type="button"
            aria-label="关闭任务图片预览"
            className="absolute inset-0 cursor-default"
            onClick={() => {
              setPreviewImageSrc('');
              setPreviewImageTitle('');
            }}
          />
          <div className="relative z-10 w-full max-w-4xl rounded-[2rem] bg-white p-4 shadow-2xl">
            <div className="mb-3 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-emerald-600">任务图片预览</p>
                <h3 className="text-xl font-black text-slate-900">{previewImageTitle}</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPreviewImageSrc('');
                  setPreviewImageTitle('');
                }}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50"
              >
                关闭
              </button>
            </div>
            <img src={previewImageSrc} alt={`${previewImageTitle}任务大图`} className="mx-auto max-h-[80vh] w-full rounded-[1.5rem] object-contain" />
          </div>
        </div>
      ) : null}
    </div>
  );
};
